import React, { useState, useEffect } from "react";
import { Menu, X, ShoppingBag } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { useLanguage } from "@/context/LanguageContext";
import { useCart } from "@/context/CartContext";

const LOGO_URL = `${process.env.REACT_APP_BACKEND_URL}/api/static/images/logo-dark.png`;

const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (el) {
    const y = el.getBoundingClientRect().top + window.scrollY - 70;
    window.scrollTo({ top: y, behavior: "smooth" });
  }
};

const Header = () => {
  const { t, lang, setLang } = useLanguage();
  const cart = useCart();
  const location = useLocation();
  const onLanding = location.pathname === "/";
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleNav = (id) => {
    setMobileOpen(false);
    if (onLanding) {
      scrollToId(id);
    } else {
      window.location.href = `/#${id}`;
    }
  };

  const NavLink = ({ id, label, testId }) => (
    <button
      onClick={() => handleNav(id)}
      data-testid={testId}
      className="font-condensed uppercase tracking-[0.2em] text-sm text-white/70 hover:text-[#00A651] transition-colors"
    >
      {label}
    </button>
  );

  return (
    <header
      data-testid="main-header"
      style={{ top: "var(--promo-h, 0px)" }}
      className={`fixed left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-black/90 backdrop-blur-xl border-b border-white/10"
          : "bg-black/40 backdrop-blur-md border-b border-transparent"
      }`}
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-12 h-20 flex items-center justify-between gap-6">
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          data-testid="header-logo-link"
          className="flex items-center shrink-0 group"
          aria-label="4keeperz"
        >
          <img
            src={LOGO_URL}
            alt="4keeperz"
            className="h-14 md:h-16 w-auto object-contain"
          />
        </button>

        <nav className="hidden md:flex items-center gap-8 lg:gap-10">
          <NavLink id="products" label={t.nav.products} testId="nav-products" />
          <NavLink id="story" label={t.nav.about} testId="nav-story" />
          <NavLink id="partners" label={t.nav.team} testId="nav-partners" />
          <Link
            to="/blog"
            data-testid="nav-blog"
            className="font-condensed uppercase tracking-[0.2em] text-sm text-white/70 hover:text-[#00A651] transition-colors"
          >
            Blog
          </Link>
          <NavLink id="contact" label={t.nav.contact} testId="nav-contact" />
        </nav>

        <div className="flex items-center gap-4">
          <div
            className="hidden md:flex items-center border border-white/15"
            data-testid="lang-switcher"
          >
            <button
              onClick={() => setLang("de")}
              data-testid="lang-de"
              className={`px-3 py-2 text-xs font-condensed uppercase tracking-widest transition-colors ${
                lang === "de" ? "bg-[#00A651] text-black" : "text-white/70 hover:text-white"
              }`}
            >
              DE
            </button>
            <button
              onClick={() => setLang("en")}
              data-testid="lang-en"
              className={`px-3 py-2 text-xs font-condensed uppercase tracking-widest transition-colors ${
                lang === "en" ? "bg-[#00A651] text-black" : "text-white/70 hover:text-white"
              }`}
            >
              EN
            </button>
          </div>

          <button
            onClick={() => cart.setOpen(true)}
            data-testid="header-cart-button"
            className="relative inline-flex items-center justify-center w-10 h-10 border border-white/15 hover:border-[#00A651] hover:text-[#00A651] text-white/80 transition-colors"
            aria-label="Cart"
          >
            <ShoppingBag className="w-4 h-4" />
            {cart.itemCount > 0 && (
              <span
                data-testid="header-cart-count"
                className="absolute -top-2 -right-2 bg-[#00A651] text-black font-display text-[11px] leading-none w-5 h-5 flex items-center justify-center"
              >
                {cart.itemCount}
              </span>
            )}
          </button>

          <button
            onClick={() => handleNav("contact")}
            data-testid="header-cta"
            className="hidden md:inline-flex items-center bg-[#00A651] hover:bg-[#008A43] text-black font-condensed uppercase tracking-[0.15em] text-sm px-5 py-2.5 transition-colors"
          >
            {t.nav.contact}
          </button>

          <button
            onClick={() => setMobileOpen((v) => !v)}
            className="md:hidden p-2"
            data-testid="mobile-menu-toggle"
            aria-label="Menu"
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-black border-t border-white/10" data-testid="mobile-menu">
          <div className="px-6 py-6 flex flex-col gap-5 items-start">
            <NavLink id="products" label={t.nav.products} testId="mobile-nav-products" />
            <NavLink id="story" label={t.nav.about} testId="mobile-nav-story" />
            <NavLink id="partners" label={t.nav.team} testId="mobile-nav-partners" />
            <Link
              to="/blog"
              onClick={() => setMobileOpen(false)}
              data-testid="mobile-nav-blog"
              className="font-condensed uppercase tracking-[0.2em] text-sm text-white/70 hover:text-[#00A651] transition-colors"
            >
              Blog
            </Link>
            <NavLink id="contact" label={t.nav.contact} testId="mobile-nav-contact" />
            <div className="flex items-center border border-white/15 w-fit mt-2">
              <button
                onClick={() => setLang("de")}
                data-testid="mobile-lang-de"
                className={`px-4 py-2 text-xs font-condensed uppercase tracking-widest ${
                  lang === "de" ? "bg-[#00A651] text-black" : "text-white/70"
                }`}
              >
                DE
              </button>
              <button
                onClick={() => setLang("en")}
                data-testid="mobile-lang-en"
                className={`px-4 py-2 text-xs font-condensed uppercase tracking-widest ${
                  lang === "en" ? "bg-[#00A651] text-black" : "text-white/70"
                }`}
              >
                EN
              </button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
